import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Search, BookOpen, Trash2, ShieldCheck, HeartPulse, FileText, CheckCircle2, Users, Paperclip } from "lucide-react";
import { HeaderAccent } from "@/lib/status";

const CATEGORY_ICON = {
  Safeguarding: ShieldCheck,
  "Health & Safety": HeartPulse,
  CQC: FileText,
  General: BookOpen,
};
const EMPTY = { title: "", category: "General", content: "", required: false };

export default function Policies() {
  const { isAdmin, user } = useAuth();
  const isOversight = ["admin", "office", "viewer"].includes(user?.role);
  const [policies, setPolicies] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [reading, setReading] = useState(null);
  const [acks, setAcks] = useState([]);
  const [summary, setSummary] = useState({});
  const [tracker, setTracker] = useState(null);

  const load = () => {
    api.get("/policies").then((r) => setPolicies(r.data));
    api.get("/policies/acks/me").then((r) => setAcks(r.data)).catch(() => {});
    if (["admin", "office", "viewer"].includes(user?.role)) {
      api.get("/policies/acks/summary").then((r) => {
        const map = {}; r.data.forEach((s) => { map[s.policy_id] = s; }); setSummary(map);
      }).catch(() => {});
    }
  };
  const accept = async (p) => {
    try { await api.post(`/policies/${p.id}/accept`); toast.success(`Accepted "${p.title}"`); setAcks((a) => Array.from(new Set([...a, p.id]))); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadAtt = async (p, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/policies/${p.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document attached"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadAtt = async (p) => {
    try { const r = await api.get(`/policies/${p.id}/attachment`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(policies.map((p) => p.category)))], [policies]);
  const filtered = policies.filter((p) =>
    (cat === "All" || p.category === cat) &&
    (p.title.toLowerCase().includes(query.toLowerCase()) || p.content.toLowerCase().includes(query.toLowerCase())));

  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/policies", form); toast.success("Policy added"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/policies/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Policies & procedures</h1>
          <p className="mt-1 text-ink-soft">Your team's reference library.</p>
          <HeaderAccent />
        </div>
        {isAdmin && (
          <button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="add-policy-button"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
            <Plus size={18} /> Add policy
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input data-testid="policy-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search policies…"
            className="w-full rounded-lg border border-border bg-white py-2.5 pl-10 pr-4 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`policy-cat-${c}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${cat === c ? "bg-brand text-white" : "border border-border bg-white text-ink-soft hover:bg-secondary"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p) => {
          const Icon = CATEGORY_ICON[p.category] || BookOpen;
          return (
            <div key={p.id} data-testid={`policy-card-${p.id}`} className={`flex flex-col rounded-xl border bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${p.required && user?.role === "carer" && !acks.includes(p.id) ? "border-coral ring-2 ring-coral/30" : "border-border"}`}>
              <div className="mb-3 flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-light text-brand"><Icon size={20} /></div>
                {isAdmin && <button onClick={() => remove(p)} data-testid={`delete-policy-${p.id}`} className="text-ink-muted hover:text-coral"><Trash2 size={16} /></button>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-brand">{p.category}</span>
                {p.required && <span data-testid={`must-read-${p.id}`} className="rounded-full bg-coral-light px-2 py-0.5 text-[10px] font-bold uppercase text-coral-dark">Must read</span>}
              </div>
              <h3 className="mt-1 font-heading text-lg font-semibold text-ink">{p.title}</h3>
              <p className="mt-2 line-clamp-3 flex-1 text-sm text-ink-soft">{p.content}</p>
              <div className="mt-4 flex items-center justify-between gap-2">
                <button onClick={() => setReading(p)} data-testid={`read-policy-${p.id}`}
                  className="text-sm font-semibold text-brand hover:text-brand-dark">Read more →</button>
                {user?.role === "carer" && (acks.includes(p.id)
                  ? <span data-testid={`accepted-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-sage-light px-2.5 py-1 text-xs font-semibold text-sage-dark"><CheckCircle2 size={13} /> Accepted</span>
                  : <button onClick={() => accept(p)} data-testid={`accept-policy-${p.id}`} className="rounded-lg bg-sage px-3 py-1.5 text-xs font-semibold text-white hover:bg-sage-dark">Read &amp; accept</button>)}
                {isOversight && summary[p.id] && (
                  <button onClick={() => setTracker(summary[p.id])} data-testid={`acceptance-${p.id}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${summary[p.id].outstanding.length === 0 ? "bg-sage-light text-sage-dark" : "bg-gold-light text-gold-dark"} hover:brightness-95`}>
                    <Users size={13} /> {summary[p.id].accepted.length}/{summary[p.id].total_carers} accepted
                  </button>
                )}
              </div>
              {(p.attachment || isAdmin) && (
                <div className="mt-3 flex items-center gap-3 border-t border-border pt-3 text-xs">
                  {p.attachment && <button onClick={() => downloadAtt(p)} data-testid={`policy-doc-${p.id}`} className="inline-flex items-center gap-1 font-medium text-slateblue hover:underline"><Paperclip size={12} /> {p.attachment.original_filename || "Document"}</button>}
                  {isAdmin && <label className="inline-flex cursor-pointer items-center gap-1 text-ink-muted hover:text-ink"><input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files[0] && uploadAtt(p, e.target.files[0])} data-testid={`upload-policy-doc-${p.id}`} /><Paperclip size={12} /> {p.attachment ? "Replace file" : "Attach file"}</label>}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="col-span-full py-12 text-center text-ink-muted">No policies found.</p>}
      </div>

      {reading && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReading(null)} />
          <div className="relative z-10 h-full w-full max-w-lg overflow-y-auto bg-white p-8 shadow-xl scrollbar-thin">
            <button onClick={() => setReading(null)} className="absolute right-5 top-5 text-ink-muted hover:text-ink"><X size={22} /></button>
            <span className="text-xs font-semibold uppercase tracking-wider text-brand">{reading.category}</span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-ink">{reading.title}</h2>
            <div className="mt-6 whitespace-pre-wrap leading-relaxed text-ink">{reading.content}</div>
          </div>
        </div>
      )}

      {tracker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setTracker(null)} />
          <div data-testid="acceptance-tracker-modal" className="relative z-10 max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-start justify-between gap-3">
              <h2 className="font-heading text-xl font-bold text-ink">Who's accepted</h2>
              <button onClick={() => setTracker(null)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <p className="mb-4 text-sm text-ink-soft">{tracker.title}</p>
            <div className="mb-4">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-sage-dark"><CheckCircle2 size={13} /> Accepted ({tracker.accepted.length})</p>
              {tracker.accepted.length === 0 ? <p className="text-sm text-ink-muted">Nobody yet.</p> : (
                <div className="space-y-1">
                  {tracker.accepted.map((a) => (
                    <div key={a.user_id} className="flex items-center justify-between rounded-lg bg-sage-light/50 px-3 py-1.5 text-sm">
                      <span className="text-ink">{a.user_name}</span>
                      <span className="text-xs text-ink-muted">{a.accepted_at ? new Date(a.accepted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gold-dark">Outstanding ({tracker.outstanding.length})</p>
              {tracker.outstanding.length === 0 ? <p className="text-sm text-sage-dark">Everyone has accepted. 🎉</p> : (
                <div className="space-y-1">
                  {tracker.outstanding.map((a) => (
                    <div key={a.user_id} className="rounded-lg bg-gold-light/50 px-3 py-1.5 text-sm text-ink">{a.user_name}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <form onSubmit={save} data-testid="policy-form" className="relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ink">Add policy</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="mb-1 block text-sm font-medium text-ink">Title *</label>
                <input required data-testid="policy-title-input" className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Category</label>
                <select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option>General</option><option>Safeguarding</option><option>Health & Safety</option><option>CQC</option>
                </select></div>
              <div><label className="mb-1 block text-sm font-medium text-ink">Content *</label>
                <textarea required data-testid="policy-content-input" rows={6} className={input} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm font-medium text-ink">
                <input type="checkbox" className="h-4 w-4 accent-coral" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="policy-required-checkbox" />
                Mark as "must read" (required reading for all befrienders)
              </label>
              <p className="text-xs text-ink-muted">Tip: after saving, use "Attach file" on the policy card to upload a PDF.</p>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button type="submit" data-testid="save-policy-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
